import { PROJECT_STAGES, type ProjectSummary } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { projectTitle, STAGE_TONE } from '../../lib/projects';
import { useCan } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ProjectWizard } from './ProjectWizard';

const projectsQuery = (status: 'active' | 'all') => ({
  queryKey: ['projects', 'list', status],
  queryFn: () => api<ProjectSummary[]>('/projects', { query: { status } }),
});

/** Раздел «Обзор» (6.1): список карточек проектов — вход в карточку, когда проект ещё не выбран */
export function ProjectsListPage() {
  const [all, setAll] = useState(false);
  const [wizard, setWizard] = useState(false);
  const canCreate = useCan('project.create');
  const q = useQuery(projectsQuery(all ? 'all' : 'active'));
  useCrumbs([]);

  const projects = q.data ?? [];
  const tops = projects.filter((p) => !p.parentId || !projects.some((x) => x.id === p.parentId));
  const subsOf = (id: string) => projects.filter((p) => p.parentId === id);

  const item = (p: ProjectSummary, sub: boolean) => {
    const objects = p.objects.filter((o) => !o.parentId);
    return (
      <li key={p.id}>
        <Link
          to="/projects/$projectId"
          params={{ projectId: p.id }}
          className={`flex min-h-16 items-center gap-3 border-t border-line py-3 pr-4 hover:bg-surface-2 ${sub ? 'pl-9' : 'pl-5'}`}
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-semibold text-ink [overflow-wrap:anywhere]">
              {sub ? p.name : projectTitle(p)}
            </span>
            <span className="text-[13px] text-ink-2">
              {[p.code, objects.map((o) => o.code ?? o.name).join(', '), p.managerName]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
              <Badge tone={STAGE_TONE[p.stage]}>{PROJECT_STAGES[p.stage]}</Badge>
              {p.status !== 'active' ? (
                <Badge tone="neutral">{p.status === 'draft' ? 'черновик' : 'архив'}</Badge>
              ) : null}
              {p.finishDate ? <span>до {date(p.finishDate)}</span> : null}
              {p.openAttention ? (
                <span className="text-warning">требует внимания: {p.openAttention}</span>
              ) : null}
            </span>
          </span>
          <ChevronRight className="size-5 shrink-0 text-ink-3" aria-hidden />
        </Link>
      </li>
    );
  };

  return (
    <Page>
      <PageHeader
        title="Обзор проектов"
        subtitle="Карточка проекта: объекты, вехи, оплаты, бюджет по разделам, сводка площадки"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAll((v) => !v)} aria-pressed={all}>
              {all ? 'Только действующие' : 'Показать архив и черновики'}
            </Button>
            {canCreate ? (
              <Button variant="primary" size="sm" onClick={() => setWizard(true)}>
                Новый проект
              </Button>
            ) : null}
          </div>
        }
      />
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data ? (
        projects.length ? (
          <Panel title="Карточки проектов" meta={`${projects.length}`} bodyClassName="p-0">
            <ul>{tops.flatMap((p) => [item(p, false), ...subsOf(p.id).map((s) => item(s, true))])}</ul>
          </Panel>
        ) : (
          <EmptyState
            title={all ? 'Проектов пока нет' : 'Действующих проектов нет'}
            action={
              canCreate ? (
                <Button variant="primary" onClick={() => setWizard(true)}>
                  Новый проект
                </Button>
              ) : undefined
            }
          >
            {canCreate
              ? 'Заведите первый проект — карточка, объекты и вехи появятся сразу.'
              : 'Когда проект заведут, его карточка появится здесь.'}
          </EmptyState>
        )
      ) : null}
      <ProjectWizard open={wizard} onOpenChange={setWizard} />
    </Page>
  );
}
